import { Component, ComponentFactoryResolver, ComponentRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DynamicRendererDirective } from '../../directives/dynamic-renderer.directive';
import { BasicInputComponent } from '../../interfaces/basic-input-component';
import { FormBuilderBasicInputModel } from '../../models/form-builder-basic-input-model';
import { FormBuilderSectionModel } from '../../models/form-builder-section-model';
import { FbCoreBasicInputChecklistInputComponent } from '../../basic-input/fb-basic-input-checklist-input/fb-basic-input-checklist-input.component';
import { FbCoreBasicInputDateFromToComponent } from '../../basic-input/fb-basic-input-date-from-to/fb-basic-input-date-from-to.component';
import { FbCoreBasicInputDateInputComponent } from '../../basic-input/fb-basic-input-date-input/fb-basic-input-date-input.component';
import { FbCoreBasicInputDividerInputComponent } from '../../basic-input/fb-basic-input-divider-input/fb-basic-input-divider-input.component';
import { FbCoreBasicInputEmailInputComponent } from '../../basic-input/fb-basic-input-email-input/fb-basic-input-email-input.component';
import { FbCoreBasicInputMultiSelectComponent } from '../../basic-input/fb-basic-input-multi-select/fb-basic-input-multi-select.component';
import { FbCoreBasicInputNumberInputComponent } from '../../basic-input/fb-basic-input-number-input/fb-basic-input-number-input.component';
import { FbCoreBasicInputParagraphComponent } from '../../basic-input/fb-basic-input-paragraph/fb-basic-input-paragraph.component';
import { FbCoreBasicInputRateInputComponent } from '../../basic-input/fb-basic-input-rate-input/fb-basic-input-rate-input.component';
import { FbCoreBasicInputSectionInputComponent } from '../../basic-input/fb-basic-input-section-input/fb-basic-input-section-input.component';
import { FbCoreBasicInputSingleSelectComponent } from '../../basic-input/fb-basic-input-single-select/fb-basic-input-single-select.component';
import { FbCoreBasicInputTextInputComponent } from '../../basic-input/fb-basic-input-text-input/fb-basic-input-text-input.component';

@Component({
  selector: 'tf-core-fb-editor-basic-component-renderer',
  templateUrl: './fb-editor-basic-component-renderer.component.html',
  styleUrls: ['./fb-editor-basic-component-renderer.component.css']
})
export class FbCoreEditorBasicComponentRendererComponent implements OnInit {
    @Input()
    section: FormBuilderSectionModel;

    @Input()
    component?: FormBuilderBasicInputModel;

    @Input()
    toggleTooltips?: boolean;

    @Input()
    form?: FormGroup;

    // Type can be any of the following: default, preview, published
    @Input()
    type?: string;

    public interval: any;

    public componentRef: ComponentRef<BasicInputComponent>;

    public editorData = '';

    @ViewChild(DynamicRendererDirective, { static: true })
    tfDynamicRenderer: DynamicRendererDirective;

    constructor(
        private componentFactoryResolver: ComponentFactoryResolver,
    ) { }

    ngOnInit(): void {
        if (this.type === undefined) {
            this.type = 'default';
        }

        this.loadComponent();

        this.refreshComponent();

        console.log('basic component renderer')
    }

    ngOnDestroy(): void {
        clearInterval(this.interval);
    }

    private loadComponent(): void {
        if (this.componentRef !== undefined) {
            this.componentRef.instance.data.sectionActive = this.section.active;
            
            this.componentRef.instance.data.readonly = this.section.readonly;

            this.componentRef.instance.data.viewType = this.type;
            
            this.componentRef.instance.data.controlName = `${this.section.label}_${this.component.label}`;
    
            if (this.toggleTooltips !== undefined) {
                this.componentRef.instance.data.toggleTooltips = this.toggleTooltips;
            }

            if (this.form !== undefined) {
                this.componentRef.instance.data.formGroup = this.form;
            }

            if (this.componentRef.instance.data.hasChanges) {
                this.updateComponentContent(this.componentRef.instance.data.editorData);
            }

            if (this.componentRef.instance.data.component === this.component) {
                return;
            }
        }

        let componentFactory = null;

        const viewContainerRef = this.tfDynamicRenderer.viewContainerRef;
        viewContainerRef.clear();

        this.componentRef = undefined;

        switch (this.component.type) {
            case 'p':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputParagraphComponent);
                break;
            case 'single_select':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputSingleSelectComponent);
                break;
            case 'text_input':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputTextInputComponent);
                break;
            case 'email_input':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputEmailInputComponent);
                break;
            case 'date_input':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputDateInputComponent);
                break;
            case 'date_from_and_to':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputDateFromToComponent);
                break;
            case 'number_input':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputNumberInputComponent);
                break;
            case 'rate_input':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputRateInputComponent);
                break;
            case 'section':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputSectionInputComponent);
                break;
            case 'multi_select':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputMultiSelectComponent);
                break;
            case 'checklist':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputChecklistInputComponent);
                break;
            case 'divider':
                componentFactory = this.componentFactoryResolver.resolveComponentFactory(FbCoreBasicInputDividerInputComponent);
                break;
            default:
                console.log('Type not found.');
                break;
        }

        this.componentRef = viewContainerRef.createComponent<BasicInputComponent>(componentFactory);

        this.componentRef.instance.data.viewType = this.type;

        this.componentRef.instance.data.controlName = `${this.section.label}_${this.component.label}`;

        if (this.toggleTooltips !== undefined) {
            this.componentRef.instance.data.toggleTooltips = this.toggleTooltips;
        }

        if (this.form !== undefined) {
            this.componentRef.instance.data.formGroup = this.form;
        }

        if (this.componentRef.instance.data.component === null) {
            this.componentRef.instance.data.component = this.component;
        }

        if (this.componentRef.instance.data.sectionActive === undefined) {
            this.componentRef.instance.data.sectionActive = this.section.active;
        }

        if (this.componentRef.instance.data.readonly === undefined) {
            this.componentRef.instance.data.readonly = this.section.readonly;
        }
    }

    private refreshComponent(): void {
        this.interval = setInterval(() => {
            this.loadComponent();
        }, 500);
    }

    private updateComponentContent(content: string): void {
        if (content !== this.component.content) {
            this.component.content = content;
        }
        console.log(this.component.content)
        this.componentRef.instance.data.hasChanges = false;
    }

}
